// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.
//
// This software is the confidential and proprietary information of
// Signal from Noise, Inc. You shall not disclose such Confidential Information
// and shall use it only in accordance with the terms of a separate license
// agreement entered into with Signal from Noise, Inc.
//
// Authors: /AUTHORS

import { type Component, For, Show } from "solid-js";
import type { FileUploadState } from "../useFileUpload";

/**
 * Props for FileUploadProgress component
 */
export interface FileUploadProgressProps {
  /** Upload state */
  upload: FileUploadState;
  /** Callback to remove/cancel upload */
  onRemove?: (uploadId: string) => void;
  /** Callback to retry failed upload */
  onRetry?: (uploadId: string) => void;
  /** Compact display mode */
  compact?: boolean;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}

/**
 * Format time duration
 */
function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "--";

  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

/**
 * Get status color classes
 */
function getStatusColor(status: FileUploadState["status"]): string {
  switch (status) {
    case "pending":
      return "text-on-background/60 dark:text-dark-on-background/60";
    case "uploading":
      return "text-primary dark:text-dark-primary";
    case "processing":
      return "text-primary dark:text-dark-primary";
    case "completed":
      return "text-success dark:text-dark-success";
    case "error":
      return "text-error dark:text-dark-error";
    default:
      return "text-on-background/60 dark:text-dark-on-background/60";
  }
}

/**
 * Get status icon
 */
function getStatusIcon(status: FileUploadState["status"]): string {
  switch (status) {
    case "pending":
      return "⏳";
    case "uploading":
      return "📤";
    case "processing":
      return "⚙️";
    case "completed":
      return "✅";
    case "error":
      return "❌";
    default:
      return "❓";
  }
}

/**
 * File upload progress component
 */
export const FileUploadProgress: Component<FileUploadProgressProps> = (props) => {
  return (
    <div class="border border-surface-decoration dark:border-dark-surface-decoration rounded-lg p-3 bg-surface/50 dark:bg-dark-surface/50">
      {/* File info header */}
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center space-x-2 min-w-0 flex-1">
          <span class="text-lg">{getStatusIcon(props.upload.status)}</span>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-on-background dark:text-dark-on-background truncate">
              {props.upload.file.name}
            </p>
            <p class="text-xs text-on-background/60 dark:text-dark-on-background/60">
              {formatFileSize(props.upload.file.size)} • {props.upload.strategy === "DIRECT" ? "Direct" : "Signed URL"}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div class="flex items-center space-x-1 ml-2">
          <Show when={props.upload.status === "error" && props.onRetry}>
            <button
              onClick={() => props.onRetry?.(props.upload.id)}
              class="p-1 text-xs bg-primary/10 hover:bg-primary/20 text-primary dark:text-dark-primary rounded transition-colors"
              title="Retry upload"
            >
              🔄
            </button>
          </Show>

          <Show
            when={
              props.onRemove &&
              (props.upload.status === "pending" ||
                props.upload.status === "error" ||
                props.upload.status === "completed")
            }
          >
            <button
              onClick={() => props.onRemove?.(props.upload.id)}
              class="p-1 text-xs bg-error/10 hover:bg-error/20 text-error dark:text-dark-error rounded transition-colors"
              title="Remove file"
            >
              ✕
            </button>
          </Show>

          <Show when={props.upload.status === "uploading" && props.onRemove}>
            <button
              onClick={() => props.onRemove?.(props.upload.id)}
              class="p-1 text-xs bg-error/10 hover:bg-error/20 text-error dark:text-dark-error rounded transition-colors"
              title="Cancel upload"
            >
              ⏹️
            </button>
          </Show>
        </div>
      </div>

      {/* Progress bar */}
      <Show when={props.upload.status === "uploading" || props.upload.status === "processing"}>
        <div class="mb-2">
          <div class="flex justify-between text-xs text-on-background/60 dark:text-dark-on-background/60 mb-1">
            <span class={getStatusColor(props.upload.status)}>
              {props.upload.status === "uploading" ? "Uploading..." : "Processing..."}
            </span>
            <span>{props.upload.progress.percentage}%</span>
          </div>

          <div class="w-full bg-surface-decoration dark:bg-dark-surface-decoration rounded-full h-2">
            <div
              class="bg-primary dark:bg-dark-primary h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${props.upload.progress.percentage}%` }}
            />
          </div>

          <Show when={!props.compact && props.upload.status === "uploading"}>
            <div class="flex justify-between text-xs text-on-background/60 dark:text-dark-on-background/60 mt-1">
              <span>
                {formatFileSize(props.upload.progress.loaded)} / {formatFileSize(props.upload.progress.total)}
              </span>
              <Show when={props.upload.progress.speed > 0}>
                <span>
                  {formatFileSize(props.upload.progress.speed)}/s •{" "}
                  {formatDuration(props.upload.progress.timeRemaining)} remaining
                </span>
              </Show>
            </div>
          </Show>
        </div>
      </Show>

      {/* Error message */}
      <Show when={props.upload.status === "error" && props.upload.error}>
        <div class="text-xs text-error dark:text-dark-error bg-error/10 dark:bg-dark-error/10 rounded p-2">
          {props.upload.error}
        </div>
      </Show>

      {/* Success message */}
      <Show when={props.upload.status === "completed"}>
        <div class="text-xs text-success dark:text-dark-success">Upload completed successfully</div>
      </Show>
    </div>
  );
};
